import type { ReactNode } from 'react';
import { View } from 'react-native';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import { Caption, Lines, Photo, type PhotoTone, useIllustrationCopy } from './illustration-parts';

export type ExampleBreedKey = 'breed' | 'breed_labrador' | 'breed_ragdoll' | 'breed_poodle';
type Scaled = { s: IllustrationScale };

export function Paper({
  s,
  x,
  y,
  width,
  height,
  angle = 0,
  padding = 7,
  children,
}: Scaled & {
  x: number;
  y: number;
  width: number;
  height: number;
  angle?: number;
  padding?: number;
  children?: ReactNode;
}) {
  return (
    <View
      className="absolute"
      style={{
        left: s(x),
        top: s(y),
        width: s(width),
        height: s(height),
        padding: s(padding),
        backgroundColor: palette.paper,
        borderColor: palette.ink + '70',
        borderWidth: s(0.8),
        borderRadius: s(3),
        transform: [{ rotate: angle + 'deg' }],
      }}
    >
      {children}
    </View>
  );
}

export function BreedPrice({
  s,
  size = 7,
  breedKey = 'breed',
}: Scaled & { size?: number; breedKey?: ExampleBreedKey }) {
  const t = useIllustrationCopy();
  return (
    <View style={{ gap: s(1.5) }}>
      <Caption s={s} size={size}>
        {t(breedKey)}
      </Caption>
      <Caption s={s} size={size} green>
        {t('price')}
      </Caption>
    </View>
  );
}

export function IllustrativeAdCard({
  s,
  width,
  photoHeight,
  tone,
  breedKey,
  compact = false,
}: Scaled & {
  width: number;
  photoHeight: number;
  tone: PhotoTone;
  breedKey: ExampleBreedKey;
  compact?: boolean;
}) {
  const t = useIllustrationCopy();
  const padding = compact ? 3 : 7;
  const border = compact ? 0.4 : 0.8;
  const imageWidth = width - padding * 2 - border * 2;
  return (
    <View
      testID="illustrative-ad-card"
      style={{
        width: s(width),
        padding: s(padding),
        gap: s(compact ? 2.4 : 5),
        borderWidth: s(border),
        borderColor: palette.ink + '70',
        borderRadius: s(3),
        backgroundColor: palette.paper,
      }}
    >
      <Photo s={s} width={imageWidth} height={photoHeight} tone={tone} />
      <View style={{ gap: s(compact ? 1.2 : 1.5) }}>
        <Caption s={s} size={compact ? 5.2 : 6.7}>
          {t(breedKey)}
        </Caption>
        <Caption s={s} size={compact ? 5 : 6.7} green>
          {t('price')}
        </Caption>
      </View>
      <Lines s={s} width={imageWidth - (compact ? 5 : 8)} rows={compact ? 2 : 3} />
    </View>
  );
}
